// 3rd party imports
import * as React from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { inject } from 'mobx-react';

// bernie
import { AnalyticsStore, ContextStore, RouteStore } from 'bernie-core';

// observability
import { ClientLogger } from 'bernie-client';
import AffiliateHubLogger from '../../util/affiliate-hub-logger';
import telemeter from '../../observability/telemeter';
import { SignupEvents, SIGNUP_TAG } from '../../observability/events/signup';

// shared-ui
import { ILocalization, LocalizedText } from '@shared-ui/localization-context';

// mobx
import { UserAuthStore, UserStore } from 'stores';

// util
import { Validation } from '../../util/Validation';
import { modifyLocaleParam } from '../../util/LocaleParamTransformer';
import { EmailDialog } from './email-dialog';
import { TaapRedirectDialog } from './taap-redirect-dialog';
import { getUserAnalyticsMicroMessage } from '../../util/analytics';

// ui
import { UitkEmailInput, UitkTextInput } from 'uitk-react-input';
import { UitkErrorSummary } from 'uitk-react-error-summary';
import { UitkHeading, UitkText } from 'uitk-react-text';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkCheckbox, UitkCheckboxGroup } from 'uitk-react-switch';
import { UitkLink } from 'uitk-react-link';
import { UitkLoader } from 'uitk-react-loader';
import { UitkPrimaryButton, UitkButtonSize } from 'uitk-react-button';
import { UitkSelect } from 'uitk-react-select';
import { UitkSpacing } from 'uitk-react-spacing';
import { LocalizedTextWrapping } from 'components/localized-text-wrapping';
import { hashSha512 } from '../../util/hash-sha512';

interface SignupAHFormProps {
  analytics?: AnalyticsStore;
  context: ContextStore;
  industries: string[];
  l10n?: ILocalization;
  routeStore: RouteStore;
  userAuth?: UserAuthStore;
  user?: UserStore;
  locale?: string;
}

const logger = new AffiliateHubLogger(ClientLogger.getLoggerWithIdentifier(`${SIGNUP_TAG}.components`));

export const SignupAHForm: React.FunctionComponent<SignupAHFormProps> = inject(
  'analytics',
  'userAuth',
)(({ analytics, context, industries, l10n, routeStore, userAuth, user, locale }) => {
  const history = useHistory();
  const [formError, setFormError] = React.useState(null);
  const [verificationEmailError, setVerificationEmailError] = React.useState(null);
  const [status, setStatus] = React.useState(null);
  const [email, setEmail] = React.useState(null);
  const [selectedIndustry, setSelectedIndustry] = React.useState(null);

  // modals control
  const [emailDialogOpen, setEmailDialogOpen] = React.useState(false);

  const [checkboxValue, setCheckboxValue] = React.useState({ tou: false, privacy: false });

  const { control, errors, handleSubmit } = useForm({ mode: 'onBlur' });

  // labels
  const emailLabel = l10n.formatText('signup.form.label.email');
  const firstNameLabel = l10n.formatText('signup.form.label.first_name');
  const lastNameLabel = l10n.formatText('signup.form.label.last_name');

  const websiteLabel = l10n.formatText('signup.form.label.website');
  const companyLabel = l10n.formatText('signup.form.label.company');
  const industryLabel = l10n.formatText('signup.form.label.industry');
  const countryLabel = l10n.formatText('signup.form.label.country');

  const submitLabel = l10n.formatText('signup.form.label.submit');

  // missing labels
  const emailMissingErrorLabel = l10n.formatText('signup.form.message.no_email');
  const firstNameMissingErrorLabel = l10n.formatText('signup.form.message.no_first_name');
  const lastNameMissingErrorLabel = l10n.formatText('signup.form.message.no_last_name');
  const websiteMissingErrorLabel = l10n.formatText('signup.form.message.no_website');
  const companyMissingErrorLabel = l10n.formatText('signup.form.message.no_company');
  const industryMissingErrorLabel = l10n.formatText('signup.form.message.no_industry');
  const countryMissingErrorLabel = l10n.formatText('signup.form.message.no_country');

  const touMissingErrorLabel = l10n.formatText('signup.form.message.no_terms_of_use');
  const privacyMissingErrorLabel = l10n.formatText('signup.form.message.no_privacy_policy');

  // invalid labels
  const emailInvalidErrorLabel = l10n.formatText('signup.form.message.invalid_email');
  const websiteInvalidErrorLabel = l10n.formatText('signup.form.message.invalid_url');

  const sendVerificationEmail = async (verificationEmail) => {
    await user
      .sendVerificationEmail(verificationEmail, modifyLocaleParam(context.locale))
      .then(() => {
        telemeter.increment(SignupEvents.VERIFICATION_EMAIL_SENT);
        logger.info(SignupEvents.VERIFICATION_EMAIL_SENT);
      })
      .catch((err) => {
        telemeter.increment(SignupEvents.VERIFICATION_EMAIL_SEND_FAIL);
        logger.error(SignupEvents.VERIFICATION_EMAIL_SEND_FAIL, JSON.stringify(err), { username: verificationEmail });
        setVerificationEmailError('Email communication failed. Please try again');
      });
  };

  const scrollWindow = (xpos, ypos) => {
    window.scrollTo(xpos, ypos);
  };

  const rfrrIdSubmitsucceeded = 'affhub_signup_submit.succeeded';
  const linkNameSubmitSuceeded = 'AffHub Signup Submit Succeeded';
  const rfrrIdAccountCreated = 'affhub_account.created';
  const linkNameAccountCreated = 'AffHub Account Created';
  const rfrrIdValidationFailed = 'affhub_signup_validation.failed';
  const linkNameValidationFailed = 'AffHub Signup Validation Failed';
  const referrerIdSignupSelected = 'affhub_signup_submit.selected';
  const linkNameSignupSelected = 'Affhub Sign Up Submit Selected';
  const rfrrIdCreationFailed = 'affhub_account_create.failed';
  const linkNameCreationFailed = 'AffHub Account Creation Failed';

  const analyticsTrackEventsMap = new Map([
    [rfrrIdSubmitsucceeded, linkNameSubmitSuceeded],
    [rfrrIdAccountCreated, linkNameAccountCreated],
    [rfrrIdValidationFailed, linkNameValidationFailed],
    [referrerIdSignupSelected, linkNameSignupSelected],
    [rfrrIdCreationFailed, linkNameCreationFailed],
  ]);

  const analyticsTrackEvents = (rfrr: string) => {
    if (typeof analytics?.trackEvent === 'function') {
      analytics.trackEvent(rfrr, analyticsTrackEventsMap.get(rfrr), null, null, true, [
        getUserAnalyticsMicroMessage(userAuth?.currentUserDetails),
      ]);
    }
  };

  React.useEffect(() => {
    if (Object.keys(errors).length) {
      analyticsTrackEvents(rfrrIdValidationFailed);
    }
  }, [errors]);

  const onSubmit = handleSubmit((fields) => {
    setStatus('LOADING');
    setEmail(fields.email);

    analyticsTrackEvents(rfrrIdSubmitsucceeded);

    if (selectedIndustry !== 'travel_agents') {
      triggerSignup(fields);
    } else {
      history.push(
        `${routeStore.pathname}?${routeStore.mergeQueryString(routeStore.search, { pwaDialog: 'taap-redirect' })}`,
      );
      setStatus(null);
    }
  });

  // tslint:disable-next-line:no-require-imports
  const { countries } = require('../../models/pz-country.json');
  const sortedCountryList = countries
    .map((item) => {
      return [item.country.printable_name, item.country.iso];
    })
    .sort();

  const triggerSignup = async (fields) => {
    telemeter.increment(SignupEvents.ATTEMPTED);

    await user.createUser(fields).then((response) => {
      setStatus(null);
      setFormError(null);

      if (response.success) {
        analyticsTrackEvents(rfrrIdAccountCreated);
        setEmailDialogOpen(true);
        sendVerificationEmail(fields.email);

        telemeter.increment(SignupEvents.SUCCESS);
        logger.info(SignupEvents.SUCCESS);

        return;
      } else {
        if (response.resBody) {
          if (response.resBody.message === 'USER_ACCOUNT_EXISTS_ERROR') {
            const errorDetail = response.resBody.detail ? ` (${response.resBody.detail})` : '';
            setFormError(l10n.formatText('signup.form.message.this_email_is_in_use') + errorDetail);
          } else {
            analyticsTrackEvents(rfrrIdCreationFailed);

            telemeter.increment(SignupEvents.FAILED);
            logger.error(SignupEvents.FAILED, JSON.stringify(response.resBody), { username: hashSha512(fields.email) });
            setFormError(l10n.formatText('common.internal_server_error'));
          }
        } else {
          analyticsTrackEvents(rfrrIdCreationFailed);

          telemeter.increment(SignupEvents.UNKNOWN_FAILURE);
          logger.error(SignupEvents.UNKNOWN_FAILURE, JSON.stringify(response), { username: hashSha512(fields.email) });

          setFormError(l10n.formatText('common.internal_server_error'));
        }
        scrollWindow(0, 0);
      }
    });
  };

  const analyticsSignupSelected = {
    id: referrerIdSignupSelected,
    callback: () => {
      analyticsTrackEvents(referrerIdSignupSelected);
    },
  };

  return (
    <>
      {emailDialogOpen && (
        <EmailDialog
          email={email}
          l10n={l10n}
          routeStore={routeStore}
          sendVerificationEmail={sendVerificationEmail}
          verificationEmailError={verificationEmailError}
          locale={locale}
        />
      )}
      <TaapRedirectDialog routeStore={routeStore} l10n={l10n} />
      <form name={'signUpForm'} onSubmit={onSubmit} method="POST">
        {formError && (
          <UitkSpacing margin={{ blockend: 'six' }}>
            <div>
              <UitkErrorSummary heading={formError} />
            </div>
          </UitkSpacing>
        )}
        <UitkLayoutFlex direction={'column'} space="three">
          <UitkLayoutFlexItem>
            <UitkHeading tag="h2" size={5}>
              <LocalizedText message="signup.form.label.tell_us_about_yourself" />
            </UitkHeading>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <Controller
              control={control}
              name="email"
              defaultValue=""
              rules={{
                required: emailMissingErrorLabel,
                pattern: {
                  value: Validation.EMAIL_REGEX,
                  message: emailInvalidErrorLabel,
                },
              }}
              render={({ onChange, onBlur, value, name }) => (
                <UitkEmailInput
                  id="signUpFormEmailInput"
                  name={name}
                  label={emailLabel}
                  value={value}
                  onChange={({ target: { value: newValue } }) => {
                    onChange(newValue);
                  }}
                  onBlur={onBlur}
                  invalid={errors.email?.message}
                  data-cy="sign-up-email-field"
                />
              )}
            />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <Controller
              control={control}
              name="firstName"
              defaultValue=""
              rules={{
                required: firstNameMissingErrorLabel,
              }}
              render={({ onChange, onBlur, value, name }) => (
                <UitkTextInput
                  id="signUpFormFirstNameInput"
                  name={name}
                  label={firstNameLabel}
                  value={value}
                  onChange={({ target: { value: newValue } }) => {
                    onChange(newValue);
                  }}
                  onBlur={onBlur}
                  invalid={errors.firstName?.message}
                  data-cy="sign-up-first-name-field"
                />
              )}
            />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <Controller
              control={control}
              name="lastName"
              defaultValue=""
              rules={{
                required: lastNameMissingErrorLabel,
              }}
              render={({ onChange, onBlur, value, name }) => (
                <UitkTextInput
                  id="signUpFormLastNameInput"
                  name={name}
                  label={lastNameLabel}
                  value={value}
                  onChange={({ target: { value: newValue } }) => {
                    onChange(newValue);
                  }}
                  onBlur={onBlur}
                  invalid={errors.lastName?.message}
                  data-cy="sign-up-last-name-field"
                />
              )}
            />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <UitkSpacing padding={{ blockstart: 'six' }}>
              <UitkHeading tag="h2" size={5}>
                <LocalizedText message="signup.form.label.tell_us_about_your_business" />
              </UitkHeading>
            </UitkSpacing>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <Controller
              control={control}
              name="website"
              defaultValue=""
              rules={{
                required: websiteMissingErrorLabel,
                pattern: {
                  value: Validation.VALID_URL,
                  message: websiteInvalidErrorLabel,
                },
              }}
              render={({ onChange, onBlur, value, name }) => (
                <UitkTextInput
                  id="signUpFormWebsiteInput"
                  name={name}
                  label={websiteLabel}
                  value={value}
                  onChange={({ target: { value: newValue } }) => {
                    onChange(newValue);
                  }}
                  onBlur={onBlur}
                  invalid={errors.website?.message}
                  data-cy="sign-up-website-field"
                />
              )}
            />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <Controller
              control={control}
              name="company"
              defaultValue=""
              rules={{
                required: companyMissingErrorLabel,
              }}
              render={({ onChange, onBlur, value, name }) => (
                <UitkTextInput
                  id="signUpFormCompanyInput"
                  name={name}
                  label={companyLabel}
                  value={value}
                  onChange={({ target: { value: newValue } }) => {
                    onChange(newValue);
                  }}
                  onBlur={onBlur}
                  invalid={errors.company?.message}
                  data-cy="sign-up-company-field"
                />
              )}
            />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <Controller
              control={control}
              name="industry"
              defaultValue=""
              rules={{
                required: industryMissingErrorLabel,
              }}
              render={({ onChange, onBlur, value, name }) => (
                <UitkSelect
                  id="signUpFormIndustryInput"
                  name={name}
                  label={industryLabel}
                  onChange={({ target: { value: newValue } }) => {
                    onChange(newValue);
                    setSelectedIndustry(newValue);
                  }}
                  onBlur={onBlur}
                  invalid={errors.industry?.message}
                  emptyState={true}
                  data-cy="sign-up-industry-field"
                >
                  {industries &&
                    industries.map((industry, i) => (
                      <option key={i} value={industry}>
                        {l10n.formatText(`affiliate.industry.${industry}`)}
                      </option>
                    ))}
                </UitkSelect>
              )}
            />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <Controller
              control={control}
              name="country"
              rules={{
                required: countryMissingErrorLabel,
              }}
              render={({ onChange, onBlur, value, name }) => (
                <UitkSelect
                  id="signUpFormCountryInput"
                  name={name}
                  label={countryLabel}
                  value={value}
                  onChange={({ target: { value: newValue } }) => {
                    onChange(newValue);
                  }}
                  onBlur={onBlur}
                  emptyState={true}
                  invalid={errors.country?.message}
                  data-cy="sign-up-country-field"
                >
                  {sortedCountryList.map((item, i) => {
                    return (
                      <option key={i} value={item[1]}>
                        {item[0]}
                      </option>
                    );
                  })}
                </UitkSelect>
              )}
            />
          </UitkLayoutFlexItem>
          <UitkCheckboxGroup group="checkboxGroup">
            <Controller
              control={control}
              name="touCheckbox"
              defaultValue={checkboxValue.tou}
              rules={{
                required: touMissingErrorLabel,
              }}
              render={({ onChange, value, name }) => (
                <UitkCheckbox
                  id="touCheckbox"
                  name={name}
                  checked={checkboxValue.tou}
                  onChange={({ target: { checked } }) => {
                    onChange(checked);
                    setCheckboxValue({ ...checkboxValue, tou: checked });
                  }}
                  errorMessage={errors.touCheckbox?.message}
                  data-cy="sign-up-terms-field"
                >
                  <LocalizedTextWrapping
                    message="signup.form.label.text.i_have_read_the_terms_of_use"
                    fieldName="terms_of_use"
                  >
                    <UitkLink inline={true}>
                      <Link to={`/${modifyLocaleParam(locale)}/terms-of-use`} target="_blank">
                        <LocalizedText message="signup.form.label.linkname.terms_of_use" />
                      </Link>
                    </UitkLink>
                  </LocalizedTextWrapping>
                </UitkCheckbox>
              )}
            />

            <Controller
              control={control}
              name="privacyCheckbox"
              defaultValue={checkboxValue.privacy}
              rules={{
                required: privacyMissingErrorLabel,
              }}
              render={({ onChange, value, name }) => (
                <UitkCheckbox
                  id="privacyCheckbox"
                  name={name}
                  checked={value}
                  onChange={({ target: { checked } }) => {
                    onChange(checked);
                    setCheckboxValue({ ...checkboxValue, privacy: checked });
                  }}
                  errorMessage={errors.privacyCheckbox?.message}
                  data-cy="sign-up-privacy-field"
                >
                  <LocalizedTextWrapping
                    message="signup.form.label.text.i_have_read_the_privacy_policy"
                    fieldName="privacy_policy"
                  >
                    <UitkLink inline={true}>
                      <Link to={`/${modifyLocaleParam(locale)}/privacy-policy`} target="_blank">
                        <LocalizedText message="signup.form.label.linkname.privacy_policy" />
                      </Link>
                    </UitkLink>
                  </LocalizedTextWrapping>
                </UitkCheckbox>
              )}
            />
          </UitkCheckboxGroup>
          <UitkLayoutFlexItem>
            <UitkPrimaryButton
              analytics={analyticsSignupSelected}
              data-cy="sign-up-submit"
              id="signUpFormSubmitButton"
              type="submit"
              className={'spacing-top-bottom-24'}
              disabled={errors.length > 0 || status === 'LOADING'}
              size={UitkButtonSize.LARGE}
            >
              {status === 'LOADING' ? '' : submitLabel}
              {status === 'LOADING' && <UitkLoader isSmall={true} isDarkBackground={true} />}
            </UitkPrimaryButton>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <UitkText>
              <LocalizedText message="signup.form.label.have_an_account" />{' '}
              <UitkLink inline={true}>
                <Link to={`/${modifyLocaleParam(locale)}/signin`}>
                  <LocalizedText message="signup.form.label.login" />
                </Link>
              </UitkLink>
            </UitkText>
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
      </form>
    </>
  );
});
